import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, FolderGit2, ListChecks, Sparkles } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import Footer from '../components/Footer';
import { LoadingSpinner } from '../components/common';
import {
  AdoptionHeatmap,
  Modernizations,
  ScoreDistribution,
  SecurityPosture,
  SystemicIssues,
  WhatAutarLearned,
  PolicyCompliancePanel,
} from '../components/fleet/FleetSections';
import { OrganizationService, type OrgDetail } from '../services/organizationService';
import {
  FleetClientService,
  type FleetInsightsDoc,
  type FleetOverview,
} from '../services/fleetClientService';
import { CampaignService } from '../services/campaignService';
import { AddRepositoriesModal } from '../components/fleet/AddRepositoriesModal';
import { PolicyService, type PolicyCompliance } from '../services/policyService';
import { RadialGauge } from '../components/fleet/FleetViz';

// Systemic-issue entity_slug → registered LLM-free task. Only mapped
// issues get an enabled "Fix across N repos" campaign button.
const ISSUE_TASK_MAP: Record<string, string> = {
  secret_detection: 'setup_secret_scanning',
  code_ownership: 'add_github_codeowners',
};

export const FleetPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [org, setOrg] = useState<OrgDetail | null>(null);
  const [overview, setOverview] = useState<FleetOverview | null>(null);
  const [insights, setInsights] = useState<FleetInsightsDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [launching, setLaunching] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [compliance, setCompliance] = useState<PolicyCompliance | null>(null);
  const [governed, setGoverned] = useState<Set<string>>(new Set());

  const refreshCompliance = useCallback(async (orgId: string) => {
    try {
      const compRes = await PolicyService.compliance(orgId);
      setCompliance(compRes.data);
      setGoverned(new Set(compRes.data.byPolicy.map((b) => b.entitySlug)));
    } catch {
      setCompliance(null);
    }
  }, []);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const [orgRes, overviewRes] = await Promise.all([
        OrganizationService.get(id),
        FleetClientService.orgOverview(id),
      ]);
      setOrg(orgRes.data);
      setOverview(overviewRes.data);
      try {
        const insightsRes = await FleetClientService.orgInsights(id);
        setInsights(insightsRes.data.insights);
      } catch {
        // Insights are seeded separately; the page degrades gracefully.
        setInsights(null);
      }
      await refreshCompliance(id);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load fleet');
    } finally {
      setLoading(false);
    }
  }, [id, refreshCompliance]);

  useEffect(() => {
    load();
  }, [load]);

  const launchCampaign = useCallback(
    async (taskType: string, issueTitle: string) => {
      if (!id || launching) return;
      setLaunching(true);
      try {
        const res = await CampaignService.create({ organizationId: id, taskType, issueTitle });
        navigate(`/campaigns/${res.data.id}`);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to launch campaign');
        setLaunching(false);
      }
    },
    [id, navigate, launching],
  );

  const handleFix = useCallback(
    (entitySlug: string, issueTitle: string) => {
      const taskType = ISSUE_TASK_MAP[entitySlug];
      if (taskType) launchCampaign(taskType, issueTitle);
    },
    [launchCampaign],
  );

  const governPractice = useCallback(
    async (slug: string, name: string, stance: 'required' | 'prohibited') => {
      if (!id) return;
      await PolicyService.upsert(id, { entitySlug: slug, entityName: name, stance });
      await refreshCompliance(id);
    },
    [id, refreshCompliance],
  );

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar pageTitle="Fleet" showDivider={true} />
        <div className="flex justify-center pt-32">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (error || !org || !overview) {
    return (
      <div className="min-h-screen">
        <Navbar pageTitle="Fleet" showDivider={true} />
        <div className="text-center pt-32 text-red-500">{error || 'Not found'}</div>
      </div>
    );
  }

  const tiles = [
    {
      icon: FolderGit2,
      value: overview.repoCount.toLocaleString(),
      label: 'repositories',
      accent: false,
    },
    {
      icon: ListChecks,
      value: overview.planTotals.totalItems.toLocaleString(),
      label: 'maintenance items',
      accent: false,
    },
    {
      icon: Sparkles,
      value: overview.planTotals.automatableItems.toLocaleString(),
      label: 'automatable without LLMs',
      accent: true,
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50/40 dark:bg-transparent">
      <Navbar pageTitle={`${org.name} — Fleet health`} showDivider={true} />
      <main className="pt-(--navbar-offset) max-w-6xl mx-auto px-4 pb-20">
        {/* Hero header */}
        <div className="relative mt-8 mb-6 overflow-hidden rounded-3xl border border-gray-200/70 dark:border-gray-800 bg-gradient-to-br from-teal-50 via-white to-white dark:from-teal-950/30 dark:via-gray-900/40 dark:to-gray-900/40 p-6 sm:p-8">
          <div className="relative flex items-start justify-between gap-4">
            <div>
              <Link
                to={`/orgs/${org.id}`}
                className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-2"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> {org.name}
              </Link>
              <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                Fleet health
              </h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {org.name} · {overview.analyzedCount} of {overview.repoCount} repositories analyzed
              </p>
            </div>
            <button
              onClick={() => setShowAdd(true)}
              className="shrink-0 inline-flex items-center gap-2 rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-teal-700 hover:shadow"
            >
              <Plus className="w-4 h-4" /> Add repositories
            </button>
          </div>

          {/* Headline metric tiles + live gauge */}
          <div className="relative mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="row-span-1 flex items-center justify-center rounded-2xl border border-gray-200/70 dark:border-gray-800 bg-white/70 dark:bg-gray-900/40 p-2">
              {overview.scoreDistribution.mean !== null ? (
                <RadialGauge value={Math.round(overview.scoreDistribution.mean)} />
              ) : (
                <span className="text-sm text-gray-400">not scanned</span>
              )}
            </div>
            {tiles.map((t) => {
              const Icon = t.icon;
              return (
                <div
                  key={t.label}
                  className={`group rounded-2xl border p-4 transition-all hover:-translate-y-0.5 ${
                    t.accent
                      ? 'border-teal-200/70 dark:border-teal-900 bg-teal-50/70 dark:bg-teal-950/30'
                      : 'border-gray-200/70 dark:border-gray-800 bg-white/70 dark:bg-gray-900/40'
                  }`}
                >
                  <Icon
                    className={`w-4 h-4 mb-2 ${t.accent ? 'text-teal-500' : 'text-gray-400 dark:text-gray-500'}`}
                  />
                  <div
                    className={`text-3xl font-bold tracking-tight ${
                      t.accent
                        ? 'text-teal-600 dark:text-teal-400'
                        : 'text-gray-900 dark:text-white'
                    }`}
                  >
                    {t.value}
                  </div>
                  <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{t.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {showAdd && org && (
          <AddRepositoriesModal
            orgId={org.id}
            onClose={() => setShowAdd(false)}
            onAttached={load}
          />
        )}

        <div className="grid gap-4 lg:grid-cols-2">
          <ScoreDistribution overview={overview} />
          {insights ? (
            <WhatAutarLearned
              insights={insights}
              reportHref={`/orgs/${org.id}/insights`}
              onGovern={governPractice}
              governed={governed}
            />
          ) : (
            <section className="p-5 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 flex items-center justify-center text-sm text-gray-500">
              Mining insights not seeded yet for this fleet.
            </section>
          )}
          {insights && compliance && (
            <PolicyCompliancePanel compliance={compliance} orgId={org.id} />
          )}
          {insights && (
            <>
              <SecurityPosture insights={insights} />
              <Modernizations
                insights={insights}
                onPush={(task, label) => launchCampaign(task, `Push ${label}`)}
              />
              <SystemicIssues
                insights={insights}
                onFix={handleFix}
                fixableSlugs={new Set(Object.keys(ISSUE_TASK_MAP))}
              />
              <AdoptionHeatmap insights={insights} orgId={org.id} />
            </>
          )}
        </div>

        <section className="mt-6 rounded-2xl border border-gray-200/70 dark:border-gray-800 bg-white dark:bg-gray-900/40 shadow-sm overflow-hidden">
          <h2 className="font-semibold text-gray-900 dark:text-white px-4 py-3 border-b border-gray-100 dark:border-gray-800">
            Repositories (worst first)
          </h2>
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <tbody>
                {overview.repositories.map((repo) => (
                  <tr
                    key={repo.id}
                    className="border-t border-gray-50 dark:border-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800/30"
                  >
                    <td className="px-4 py-2">
                      <Link
                        to={`/repository/${repo.id}`}
                        className="text-teal-700 dark:text-teal-400 hover:underline"
                      >
                        {repo.name}
                      </Link>
                      {repo.isMonorepo && (
                        <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300">
                          monorepo · {repo.projectCount} projects
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 w-20 text-gray-900 dark:text-gray-200">
                      {repo.latestAnalysisScore ?? '—'}
                    </td>
                    <td className="px-4 py-2 w-40 text-xs text-gray-500 dark:text-gray-400">
                      {repo.planTotalItems !== null
                        ? `${repo.planTotalItems} items · ${repo.planAutomatable} auto`
                        : 'no plan yet'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};
